import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SHIFT_META, WEEKDAY_LABELS } from './constants';

const STATUS_OPTS = ['preferred', 'available', 'unavailable'];
const STATUS_CLS = {
  preferred: 'bg-[#1D9E75] text-white border-[#1D9E75]',
  available: 'bg-[#3B82C4] text-white border-[#3B82C4]',
  unavailable: 'bg-[#D25454] text-white border-[#D25454]',
};

export const BulkActionsDialog = ({ open, onOpenChange, dates, selectedDates, onApply }) => {
  const [target, setTarget] = useState('all');
  const [weekdays, setWeekdays] = useState([]);
  const [everyOther, setEveryOther] = useState(false);
  const [action, setAction] = useState('set');
  const [allDay, setAllDay] = useState(false);
  const [shifts, setShifts] = useState({});
  const [restriction, setRestriction] = useState('');

  const toggleWeekday = (i) =>
    setWeekdays((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));

  const toggleShift = (key, status) =>
    setShifts((prev) => {
      const next = { ...prev };
      if (next[key] === status) delete next[key];
      else next[key] = status;
      return next;
    });

  const computeTargets = () => {
    let list;
    if (target === 'selected') list = dates.filter((d) => selectedDates.has(d));
    else {
      list = dates.filter((d) => {
        const wd = new Date(d + 'T00:00:00').getDay();
        if (target === 'all') return true;
        if (target === 'weekdays') return wd >= 1 && wd <= 5;
        if (target === 'weekends') return wd === 0 || wd === 6;
        if (target === 'custom') return weekdays.includes(wd);
        return false;
      });
    }
    if (everyOther && target !== 'selected') {
      // Keep occurrences in alternating weeks, starting from the first match
      const weekIndex = (d) => Math.floor((new Date(d + 'T00:00:00') - new Date(dates[0] + 'T00:00:00')) / (7 * 86400000));
      const firstWeek = list.length ? weekIndex(list[0]) : 0;
      list = list.filter((d) => (weekIndex(d) - firstWeek) % 2 === 0);
    }
    return list;
  };

  const handleApply = () => {
    const targets = computeTargets();
    if (action === 'clear') {
      onApply(targets, null);
    } else {
      onApply(targets, {
        unavailable_all_day: allDay,
        shifts: allDay ? {} : shifts,
        restriction: restriction.trim(),
        note: '',
      });
    }
    onOpenChange(false);
  };

  const targetCount = computeTargets().length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" data-testid="bulk-actions-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Entry</DialogTitle>
          <DialogDescription>Apply the same availability to many dates at once, then edit individual exceptions.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">Apply to</label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="bg-background" data-testid="bulk-target-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background">
                <SelectItem value="all">All dates</SelectItem>
                <SelectItem value="weekdays">Weekdays (Mon–Fri)</SelectItem>
                <SelectItem value="weekends">Weekends (Sat–Sun)</SelectItem>
                <SelectItem value="custom">Selected weekdays…</SelectItem>
                <SelectItem value="selected" disabled={selectedDates.size === 0}>
                  Selected dates ({selectedDates.size})
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {target === 'custom' && (
            <div className="flex flex-wrap gap-1.5" data-testid="bulk-weekday-picker">
              {WEEKDAY_LABELS.map((label, i) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => toggleWeekday(i)}
                  className={`min-w-[44px] h-10 px-2 rounded-lg border text-xs font-semibold ${
                    weekdays.includes(i) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background'
                  }`}
                  data-testid={`bulk-weekday-${label.toLowerCase()}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {target !== 'selected' && (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="text-sm">Every other week only <span className="text-xs text-muted-foreground">(e.g. every other Saturday)</span></div>
              <Switch checked={everyOther} onCheckedChange={setEveryOther} data-testid="bulk-every-other" />
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Action</label>
            <div className="flex gap-2">
              <Button type="button" variant={action === 'set' ? 'default' : 'outline'} className="flex-1" onClick={() => setAction('set')} data-testid="bulk-action-set">
                Set availability
              </Button>
              <Button type="button" variant={action === 'clear' ? 'destructive' : 'outline'} className="flex-1" onClick={() => setAction('clear')} data-testid="bulk-action-clear">
                Clear dates
              </Button>
            </div>
          </div>

          {action === 'set' && (
            <>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="text-sm font-medium text-[#912F2F]">Unavailable all day</div>
                <Switch checked={allDay} onCheckedChange={(c) => { setAllDay(c); if (c) setShifts({}); }} data-testid="bulk-all-day" />
              </div>
              {!allDay && (
                <div className="space-y-2">
                  {SHIFT_META.map((s) => (
                    <div key={s.key} className="flex items-center gap-2">
                      <span className="text-xs font-semibold w-20">{s.label}</span>
                      <div className="flex gap-1.5 flex-1">
                        {STATUS_OPTS.map((st) => (
                          <button
                            key={st}
                            type="button"
                            onClick={() => toggleShift(s.key, st)}
                            className={`flex-1 h-9 rounded-md border text-[11px] font-semibold capitalize ${
                              shifts[s.key] === st ? STATUS_CLS[st] : 'bg-background'
                            }`}
                            data-testid={`bulk-shift-${s.key}-${st}`}
                          >
                            {st}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <Input
                    value={restriction}
                    onChange={(e) => setRestriction(e.target.value)}
                    placeholder="Optional restriction, e.g. 'Available after 4:00 PM'"
                    maxLength={300}
                    data-testid="bulk-restriction"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleApply} disabled={targetCount === 0} data-testid="bulk-apply">
            Apply to {targetCount} date{targetCount === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
